// src/pages/Account.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import PageHeader from "../components/PageHeader";

type UserProfile = {
  uid: string;          // mirrors auth UID (doc id)
  email: string;        // from auth
  firstName?: string;
  lastName?: string;
  displayName?: string;
  createdAt?: any;
  updatedAt?: any;
  emailLower?: string;
  nameLower?: string;
};

export default function Account() {
  const [uid, setUid] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");

  // Tracks whether the user has manually typed in displayName.
  const [displayTouched, setDisplayTouched] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" | "info" | "warning" }>({
    open: false,
    msg: "",
    sev: "success",
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUid(null);
        setEmail(null);
        setLoading(false);
        return;
      }
      setUid(user.uid);
      setEmail(user.email ?? null);

      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data() as UserProfile;
        const f = d.firstName ?? "";
        const l = d.lastName ?? "";
        const dn = d.displayName ?? "";
        setFirstName(f);
        setLastName(l);
        setDisplayName(dn);
        setDisplayTouched(!!dn); // if a saved display name exists, treat as user-provided
      } else {
        // no profile yet; prefill display name with auth displayName if present
        const fallback = (user.displayName ?? "").trim();
        setDisplayName(fallback);
        setDisplayTouched(!!fallback);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Auto-fill displayName while it is empty and not touched
  useEffect(() => {
    const dn = (displayName ?? "").trim();
    if (!displayTouched && dn.length === 0) {
      const auto = `${firstName} ${lastName}`.trim();
      setDisplayName(auto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName]);

  const canSave = useMemo(() => !!uid && !!email, [uid, email]);

  const handleSave = async () => {
    if (!uid || !email) return;
    try {
      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();
      const trimmedDisplay = (displayName || "").trim() || `${trimmedFirst} ${trimmedLast}`.trim();

      const profile: Partial<UserProfile> = {
        uid,
        email,
        firstName: trimmedFirst || undefined,
        lastName: trimmedLast || undefined,
        displayName: trimmedDisplay || undefined,
        emailLower: email.toLowerCase(),
        nameLower: (`${trimmedFirst} ${trimmedLast}`.trim() || trimmedDisplay).toLowerCase() || undefined,
        updatedAt: serverTimestamp(),
      };

      const ref = doc(db, "users", uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        profile.createdAt = serverTimestamp();
      }

      await setDoc(ref, profile, { merge: true });
      setSnack({ open: true, msg: "Profile saved.", sev: "success" });
      // display name is now considered user-defined
      setDisplayTouched(true);
    } catch (e) {
      console.error("Account: save failed", e);
      setSnack({ open: true, msg: "Failed to save profile.", sev: "error" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!uid) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">You need to sign in to view your account.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader title="Your Account" subtitle="Manage the profile details shared with collaborators" />

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Email"
              value={email ?? ""}
              InputProps={{ readOnly: true }}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
              />
            </Stack>

            <TextField
              label="Display name"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setDisplayTouched(true);
              }}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                Save profile
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={2400}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
